import { CSSProperties } from 'react';

/**
 * Stepper — progress indicator using oversized serif numerals.
 * Canonical pattern from the style guide for multi-step flows (onboarding,
 * trip-build wizard, etc.).
 *
 * Active step: numeral in --accent, label in --text, optional sub-label shown.
 * Inactive steps: numeral in --line, label in --text-soft.
 *
 * steps: array of strings or { label, sub } objects.
 * activeIndex: 0-based index of the current step.
 */
export interface StepperStep {
  label: string;
  /** Optional sub-label shown only on the active step. */
  sub?: string;
}

export interface StepperProps {
  /** Array of step labels or { label, sub } objects. */
  steps: Array<string | StepperStep>;
  /** 0-based index of the current active step. Default 0. */
  activeIndex?: number;
  style?: CSSProperties;
}

export function Stepper({ steps = [], activeIndex = 0, style }: StepperProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, ...style }}>
      {steps.map((step, i) => {
        const active = i === activeIndex;
        const label = typeof step === 'string' ? step : step.label;
        const sub   = typeof step === 'string' ? null  : step.sub;
        return (
          <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <span
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'var(--fs-stepper-numeral)',
                lineHeight: 0.85,
                color: active ? 'var(--accent)' : 'var(--line)',
                flexShrink: 0,
                width: 42,
                userSelect: 'none',
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={active ? {
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-subhead)',
                  lineHeight: 1.2,
                  color: 'var(--text)',
                } : {
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-subhead)',
                  lineHeight: 1.2,
                  color: 'var(--text-soft)',
                }}
              >
                {label}
              </div>
              {sub && active ? (
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-meta)',
                    lineHeight: 1.4,
                    color: 'var(--text-dim)',
                    marginTop: 3,
                  }}
                >
                  {sub}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Stepper;
